import { useCallback, useState, type FormEvent } from 'react';
import type { DeskTicketRule } from '@detect/shared';
import { TicketRulesEditor } from './TicketRulesEditor';
import { ruleProblems } from './ticketRules';
import { integrationService, type IntegrationView } from '../../services/integrations';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { deskWorkspaceNote } from './deskWorkspace';
import { normalizeDeskWorkspace } from '@detect/shared';

/**
 * Outbound integrations (Desk, Dispatch, webhooks). Credentials are
 * WRITE-ONLY (#48): the form takes the VALUE once, the server stores it in
 * Secret Manager under a name IT derives, and the value is never shown
 * again — an edit leaves a field blank to keep the stored value, or types a
 * new one to rotate it. Tenants never see or choose secret names.
 */
export function IntegrationsTab() {
  const { tenantId, hasRole } = useTenant();
  // Integration mutations are ACCOUNT_ADMIN on the API (they wire
  // outbound credentials); the affordances match.
  const canMutate = hasRole('account_admin');
  const [editing, setEditing] = useState<WithId<IntegrationView> | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      integrationService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<IntegrationView>>(fetchPage, 'all');

  const remove = async (integration: WithId<IntegrationView>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete integration ${integration.name}?`)) return;
    setActionError(null);
    try {
      await integrationService.remove(tenantId, integration.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {canMutate && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing('new')}
          >
            New integration
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No integrations"
          description="Connect Desk, Dispatch, or a custom webhook to deliver alerts."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Enabled</th>
                {canMutate && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((i) => (
                <tr key={i.id}>
                  <td>
                    {i.name}
                    {deskWorkspaceNote(i) && <div className="text-muted">{deskWorkspaceNote(i)}</div>}
                  </td>
                  <td>{i.kind}</td>
                  <td>{i.enabled ? 'yes' : 'no'}</td>
                  {canMutate && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setEditing(i)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(i)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {editing !== null && (
        <IntegrationModal
          integration={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

type Kind = 'desk' | 'dispatch' | 'webhook';

function IntegrationModal({
  integration,
  onClose,
  onSaved,
}: {
  integration: WithId<IntegrationView> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [kind, setKind] = useState<Kind>(integration?.kind ?? 'webhook');
  const [name, setName] = useState(integration?.name ?? '');
  const [enabled, setEnabled] = useState(integration?.enabled ?? true);
  // Desk
  const [deskWorkspace, setDeskWorkspace] = useState(
    integration?.kind === 'desk' ? (integration.deskWorkspace ?? '') : ''
  );
  const [serviceToken, setServiceToken] = useState('');
  const [ackSync, setAckSync] = useState(
    integration?.kind === 'desk' ? integration.ackSync : true
  );
  const [resolveOnTicketClose, setResolveOnTicketClose] = useState(
    integration?.kind === 'desk' ? integration.resolveOnTicketClose : false
  );
  const [ticketRules, setTicketRules] = useState<DeskTicketRule[]>(
    integration?.kind === 'desk' ? (integration.ticketRules ?? []) : []
  );
  // Dispatch
  const [serviceProviderId, setServiceProviderId] = useState(
    integration?.kind === 'dispatch' ? integration.serviceProviderId : ''
  );
  const [apiKey, setApiKey] = useState('');
  const [hmacSecret, setHmacSecret] = useState('');
  // Desk's second credential (#88) has its own state: a value typed for another kind must never ride along.
  const [signingSecret, setSigningSecret] = useState('');
  const [autoResolveOnCompletion, setAutoResolveOnCompletion] = useState(
    integration?.kind === 'dispatch' ? integration.autoResolveOnCompletion : true
  );
  // Webhook
  const [url, setUrl] = useState(
    integration?.kind === 'webhook' ? integration.url : ''
  );
  const [payloadTemplate, setPayloadTemplate] = useState(
    integration?.kind === 'webhook'
      ? integration.payloadTemplate
      : '{"alert": "{{alert.id}}", "severity": "{{alert.severity}}", "resource": "{{alert.resourceName}}", "event": "{{alert.event}}", "link": "{{alert.link}}"}'
  );
  const [secretHeaderName, setSecretHeaderName] = useState(
    integration?.kind === 'webhook' ? (integration.secretHeaderName ?? '') : ''
  );
  const [secretHeaderValue, setSecretHeaderValue] = useState('');
  const [events, setEvents] = useState<string[]>(
    integration?.kind === 'webhook' ? [...integration.events] : ['raised', 'resolved']
  );
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  /** A blank secret field on EDIT means "keep the stored value" — it is
   *  omitted from the request, never sent as an empty string. */
  const secret = (key: string, value: string): Record<string, string> =>
    value.trim() === '' ? {} : { [key]: value };

  const buildPayload = (): Record<string, unknown> => {
    switch (kind) {
      case 'desk':
        return {
          kind,
          name,
          enabled,
          deskWorkspace: normalizeDeskWorkspace(deskWorkspace),
          ...secret('serviceToken', serviceToken),
          ...secret('hmacSecret', signingSecret),
          ackSync,
          resolveOnTicketClose,
          // Always round-tripped: the update replaces the whole non-secret config.
          ticketRules,
        };
      case 'dispatch':
        return {
          kind,
          name,
          enabled,
          serviceProviderId,
          ...secret('apiKey', apiKey),
          ...secret('hmacSecret', hmacSecret),
          autoResolveOnCompletion,
        };
      case 'webhook':
        return {
          kind,
          name,
          enabled,
          url,
          payloadTemplate,
          events,
          // Round-trip fields this form does not edit yet — the update
          // replaces the whole config, so omitting them would DELETE
          // an API-configured method/headers/auth-header pairing.
          ...(integration?.kind === 'webhook'
            ? {
                method: integration.method,
                ...(integration.headers ? { headers: integration.headers } : {}),
              }
            : {}),
          ...(secretHeaderName.trim() !== ''
            ? { secretHeaderName: secretHeaderName.trim() }
            : {}),
          ...secret('secretHeaderValue', secretHeaderValue),
        };
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    if (kind === 'desk') {
      const problems = ruleProblems(ticketRules);
      if (problems.length > 0) {
        setError(new Error(problems[0]));
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      if (integration) {
        await integrationService.update(tenantId, integration.id, buildPayload());
      } else {
        await integrationService.create(tenantId, buildPayload());
      }
      onSaved();
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={integration ? `Edit ${integration.name}` : 'New integration'}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        {integration?.secretsNeedReentry && (
          <p role="alert" className="text-muted">
            This integration&apos;s credentials predate the secure credential store and
            will not be used. Re-enter them below to resume delivery.
          </p>
        )}
        <div className="form-field">
          <label htmlFor="int-kind">Kind</label>
          <select
            id="int-kind"
            name="kind"
            value={kind}
            disabled={integration !== null}
            onChange={(e) => setKind(e.target.value as Kind)}
          >
            <option value="desk">Desk (ticketing)</option>
            <option value="dispatch">Dispatch (field service)</option>
            <option value="webhook">Webhook</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="int-name">Name</label>
          <input
            id="int-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {kind === 'desk' && (
          <>
            <div className="form-field">
              <label htmlFor="int-desk-tenant">Desk workspace</label>
              <input
                id="int-desk-tenant"
                name="deskWorkspace"
                required
                value={deskWorkspace}
                onChange={(e) => setDeskWorkspace(e.target.value)}
              />
              <p className="text-muted">
                The name or address of your Desk workspace, for example <code>acme</code> or <code>acme.desk.meshtek.io</code>.
              </p>
              {integration && deskWorkspaceNote(integration) && (
                <p className="text-muted">
                  {normalizeDeskWorkspace(deskWorkspace) !== (integration.kind === 'desk' ? (integration.deskWorkspace ?? '') : '')
                    ? 'Changing the workspace means it has to be confirmed again.'
                    : deskWorkspaceNote(integration)}
                </p>
              )}
            </div>
            <SecretField
              id="int-desk-secret"
              name="serviceToken"
              label="Service token"
              value={serviceToken}
              onChange={setServiceToken}
              isSet={integration?.kind === 'desk' && integration.serviceTokenSet}
            />
            <SecretField
              id="int-desk-signing"
              name="hmacSecret"
              label="Signing secret"
              value={signingSecret}
              onChange={setSigningSecret}
              isSet={integration?.kind === 'desk' && integration.hmacSet}
            />
            <p className="text-muted">
              Desk gives you both values, a service token and a signing secret. Alerts are only sent once both are
              entered, and neither is shown again after you save.
            </p>
            <div className="form-field form-field-inline">
              <input
                id="int-desk-acksync"
                name="ackSync"
                type="checkbox"
                checked={ackSync}
                onChange={(e) => setAckSync(e.target.checked)}
              />
              <label htmlFor="int-desk-acksync">Ack in Desk acknowledges here</label>
            </div>
            <div className="form-field form-field-inline">
              <input
                id="int-desk-ticketclose"
                name="resolveOnTicketClose"
                type="checkbox"
                checked={resolveOnTicketClose}
                onChange={(e) => setResolveOnTicketClose(e.target.checked)}
              />
              <label htmlFor="int-desk-ticketclose">
                Ticket close resolves the alert (default: the metric is the truth)
              </label>
            </div>
            <TicketRulesEditor rules={ticketRules} onChange={setTicketRules} />
          </>
        )}

        {kind === 'dispatch' && (
          <>
            <div className="form-field">
              <label htmlFor="int-sp">Service provider id</label>
              <input
                id="int-sp"
                name="serviceProviderId"
                required
                value={serviceProviderId}
                onChange={(e) => setServiceProviderId(e.target.value)}
              />
            </div>
            <SecretField
              id="int-apikey"
              name="apiKey"
              label="API key"
              value={apiKey}
              onChange={setApiKey}
              isSet={integration?.kind === 'dispatch' && integration.apiKeySet}
            />
            <SecretField
              id="int-hmac"
              name="hmacSecret"
              label="HMAC signing secret"
              value={hmacSecret}
              onChange={setHmacSecret}
              isSet={integration?.kind === 'dispatch' && integration.hmacSet}
            />
            <div className="form-field form-field-inline">
              <input
                id="int-autoresolve"
                name="autoResolveOnCompletion"
                type="checkbox"
                checked={autoResolveOnCompletion}
                onChange={(e) => setAutoResolveOnCompletion(e.target.checked)}
              />
              <label htmlFor="int-autoresolve">
                Request completion resolves the alert (re-opens if it re-fires)
              </label>
            </div>
          </>
        )}

        {kind === 'webhook' && (
          <>
            <div className="form-field">
              <label htmlFor="int-url">URL (https only)</label>
              <input
                id="int-url"
                name="url"
                type="url"
                required
                placeholder="https://hooks.example.com/detect"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="int-template">
                JSON payload template ({'{{alert.*}}'} tokens)
              </label>
              <textarea
                id="int-template"
                name="payloadTemplate"
                rows={4}
                required
                value={payloadTemplate}
                onChange={(e) => setPayloadTemplate(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="int-header-name">Auth header name (optional)</label>
              <input
                id="int-header-name"
                name="secretHeaderName"
                placeholder="Authorization"
                value={secretHeaderName}
                onChange={(e) => setSecretHeaderName(e.target.value)}
              />
            </div>
            {secretHeaderName.trim() !== '' && (
              <SecretField
                id="int-header-value"
                name="secretHeaderValue"
                label="Auth header value"
                value={secretHeaderValue}
                onChange={setSecretHeaderValue}
                isSet={integration?.kind === 'webhook' && integration.secretHeaderSet === true}
              />
            )}
            <div className="form-field">
              <label htmlFor="int-events">Lifecycle events</label>
              <select
                id="int-events"
                name="events"
                multiple
                size={4}
                value={events}
                onChange={(e) =>
                  setEvents(Array.from(e.target.selectedOptions).map((o) => o.value))
                }
              >
                <option value="raised">raised</option>
                <option value="escalated">escalated</option>
                <option value="acknowledged">acknowledged</option>
                <option value="resolved">resolved</option>
              </select>
            </div>
          </>
        )}

        <div className="form-field form-field-inline">
          <input
            id="int-enabled"
            name="enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="int-enabled">Enabled</label>
        </div>
        <div className="modal-footer">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : integration ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A write-only credential input: never pre-filled, never displayed. On edit
 *  a blank value keeps the stored credential, so it is only
 *  required while nothing is on file. */
function SecretField({
  id,
  name,
  label,
  value,
  onChange,
  isSet,
}: {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  isSet: boolean;
}) {
  return (
    <div className="form-field">
      <label htmlFor={id}>
        {label} {isSet ? '(set — leave blank to keep)' : '(not set)'}
      </label>
      <input
        id={id}
        name={name}
        type="password"
        autoComplete="new-password"
        required={!isSet}
        placeholder={isSet ? '••••••••' : ''}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
